import React, { Fragment, useState, useEffect } from "react";
import styled from "styled-components";
import { Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import { combineFromCategories } from "_helpers";
import { reportsBehindOrdersActions, variantsActions } from "_actions";

import Table from "components/Table";
import ConfirmationModal from "components/ConfirmationModal";
import CustomSelect from "./CustomSelect";

const StyledActionsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
`;

const createCategoriesOptions = () => {
  const categories = ["ACCOUNTING", "EIN", "EIN_INC", "INC", "INC_States", "TRADEMARKS", "PASSPORT", "VITAL"];
  return categories.map((category) => ({
    value: category,
    label: category,
  }));
};

const createProductsOptions = ({ category, allProductCodes }) => {
  const productCodesByCategory = allProductCodes[category] || [];
  return productCodesByCategory.map(({ code, customerName }) => ({
    value: code,
    label: customerName,
  }));
};

const createProcessingOptions = ({ category, processingItems }) => {
  const processingItemsByCategory = processingItems[category] || [];
  return processingItemsByCategory.map(({ code, value }) => ({
    value: code,
    label: value,
  }));
};

const isActionDisabled = ({ rule, loading }) => {
  return (
    Object.keys(rule).some(
      (key) =>
        !["id", "processingError", "errorStatus"].includes(key) &&
        (Array.isArray(rule[key]) ? !rule[key].length : !rule[key])
    ) || loading
  );
};

const getColumns = ({
  posting,
  deleting,
  categoriesOptions,
  allProductCodes,
  allProcessingStatuses,
  allProcessingErrors,
  allProcessingErrorStatuses,
  processingErrorsMap,
  processingErrorStatusesMap,
  newRule,
  editRule,
  handleNewChange,
  handleEditChange,
  handleAdd,
  handleEdit,
  handleSave,
  handleDelete,
}) => {
  return [
    {
      dataIndex: "category",
      title: <b>Category</b>,
      width: "12%",
      breakWords: true,
      render: (row) => {
        if (editRule && row?.id === editRule?.id) {
          return (
            <CustomSelect
              name="category"
              closeMenuOnSelect
              options={categoriesOptions}
              value={editRule?.category}
              onSelect={handleEditChange}
            />
          );
        }

        if (row?.id) {
          return <span>{row?.category}</span>;
        }

        return (
          <CustomSelect
            name="category"
            closeMenuOnSelect
            options={categoriesOptions}
            value={newRule?.category}
            onSelect={handleNewChange}
          />
        );
      },
    },
    {
      dataIndex: "products",
      title: <b>Product(s)</b>,
      width: "20%",
      breakWords: true,
      render: (row) => {
        if (editRule && row?.id === editRule?.id) {
          return (
            <CustomSelect
              name="products"
              isMulti
              options={createProductsOptions({ category: editRule?.category, allProductCodes })}
              value={editRule?.products}
              onSelect={handleEditChange}
            />
          );
        }

        if (row?.id) {
          return row?.products?.map(({ code, customerName }) => (
            <Fragment key={code}>
              {customerName}
              <br />
            </Fragment>
          ));
        }

        return (
          <CustomSelect
            name="products"
            isMulti
            options={createProductsOptions({ category: newRule?.category, allProductCodes })}
            value={newRule?.products}
            onSelect={handleNewChange}
          />
        );
      },
    },
    {
      dataIndex: "statuses",
      title: <b>Status(es)</b>,
      width: "20%",
      render: (row) => {
        if (editRule && row?.id === editRule?.id) {
          return (
            <CustomSelect
              name="statuses"
              isMulti
              options={createProcessingOptions({
                category: editRule?.category,
                processingItems: allProcessingStatuses,
              })}
              value={editRule?.statuses}
              onSelect={handleEditChange}
            />
          );
        }

        if (row?.id) {
          return <span>{row?.statuses?.join(", ")}</span>;
        }

        return (
          <CustomSelect
            name="statuses"
            isMulti
            options={createProcessingOptions({
              category: newRule?.category,
              processingItems: allProcessingStatuses,
            })}
            value={newRule?.statuses}
            onSelect={handleNewChange}
          />
        );
      },
    },
    {
      dataIndex: "processingError",
      title: <b>Error</b>,
      width: "13%",
      render: (row) => {
        if (editRule && row?.id === editRule?.id) {
          return (
            <CustomSelect
              name="processingError"
              isDisabled={!editRule?.statuses?.includes("Error")}
              options={createProcessingOptions({
                category: editRule?.category,
                processingItems: allProcessingErrors,
              })}
              value={editRule?.processingError}
              onSelect={handleEditChange}
            />
          );
        }

        if (row?.id && row?.processingError) {
          return <span>{processingErrorsMap[row?.processingError] || row?.processingError}</span>;
        }

        if (row?.id && !row?.processingError) {
          return <span>—</span>;
        }

        return (
          <CustomSelect
            name="processingError"
            isDisabled={!newRule?.statuses?.includes("Error")}
            options={createProcessingOptions({
              category: newRule?.category,
              processingItems: allProcessingErrors,
            })}
            value={newRule?.processingError}
            onSelect={handleNewChange}
          />
        );
      },
    },
    {
      dataIndex: "errorStatus",
      title: <b>Error Status</b>,
      width: "13%",
      render: (row) => {
        if (editRule && row?.id === editRule?.id) {
          return (
            <CustomSelect
              name="errorStatus"
              isDisabled={!editRule?.statuses?.includes("Error")}
              options={createProcessingOptions({
                category: editRule?.category,
                processingItems: allProcessingErrorStatuses,
              })}
              value={editRule?.errorStatus}
              onSelect={handleEditChange}
            />
          );
        }

        if (row?.id && row?.errorStatus) {
          return <span>{processingErrorStatusesMap[row?.errorStatus] || row?.errorStatus}</span>;
        }

        if (row?.id && !row?.errorStatus) {
          return <span>—</span>;
        }

        return (
          <CustomSelect
            name="errorStatus"
            isDisabled={!newRule?.statuses?.includes("Error")}
            options={createProcessingOptions({
              category: newRule?.category,
              processingItems: allProcessingErrorStatuses,
            })}
            value={newRule?.errorStatus}
            onSelect={handleNewChange}
          />
        );
      },
    },
    {
      dataIndex: "timeToExceed",
      title: <b>Time To Exceed</b>,
      width: "10%",
      render: (row) => {
        if (editRule && row?.id === editRule?.id) {
          return (
            <Form.Control
              name="timeToExceed"
              type="number"
              min="0"
              value={editRule?.timeToExceed || ""}
              onChange={handleEditChange}
            />
          );
        }

        if (row.id) {
          return <span>{row?.timeToExceed}</span>;
        }

        return (
          <Form.Control
            name="timeToExceed"
            type="number"
            min="0"
            value={newRule?.timeToExceed || ""}
            onChange={handleNewChange}
          />
        );
      },
    },
    {
      dataIndex: "actions",
      title: <b>Actions</b>,
      width: "12%",
      render: (row) => {
        if (row?.id) {
          return (
            <StyledActionsContainer>
              {row?.id !== editRule?.id && (
                <Button
                  variant="outline-info"
                  disabled={deleting || posting}
                  onClick={() => {
                    handleEdit({ ...row, products: row?.products?.map(({ code }) => code) });
                  }}
                >
                  Edit
                </Button>
              )}
              {editRule && row?.id === editRule?.id && (
                <Button
                  variant="outline-success"
                  disabled={isActionDisabled({ rule: editRule, loading: posting || deleting })}
                  onClick={handleSave}
                >
                  Save
                </Button>
              )}
              <Button
                variant="outline-danger"
                disabled={deleting || posting}
                onClick={() => {
                  handleDelete(row?.id);
                }}
              >
                Delete
              </Button>
            </StyledActionsContainer>
          );
        }

        return (
          <StyledActionsContainer>
            <Button
              variant="primary"
              disabled={isActionDisabled({ rule: newRule, loading: posting || deleting })}
              onClick={handleAdd}
            >
              + Add
            </Button>
          </StyledActionsContainer>
        );
      },
    },
  ];
};

const initialRule = {
  category: "",
  products: [],
  statuses: [],
  processingError: null,
  errorStatus: null,
  timeToExceed: 0,
};

export default function BehindOrders() {
  const dispatch = useDispatch();

  const allProductCodes = useSelector(({ variants }) => variants.allProductCodes);
  const allProcessingStatuses = useSelector(({ variants }) => variants.allProcessingStatuses);
  const allProcessingErrors = useSelector(({ variants }) => variants.allProcessingErrors);
  const allProcessingErrorStatuses = useSelector(({ variants }) => variants.allProcessingErrorStatuses);

  const { posting, deleting, behindOrderRules } = useSelector(({ reportsBehindOrders }) => reportsBehindOrders);

  const [newRule, setNewRule] = useState(initialRule);
  const [editRule, setEditRule] = useState(null);
  const [deleteRule, setDeleteRule] = useState(null);
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);

  const categoriesOptions = createCategoriesOptions();
  const processingErrorsMap = combineFromCategories(allProcessingErrors);
  const processingErrorStatusesMap = combineFromCategories(allProcessingErrorStatuses);

  const handleNewChange = (e) => {
    const { name, value } = e.target || {};
    const errorFields = {};

    if (!value?.includes("Error") && name === "statuses") {
      errorFields.processingError = null;
      errorFields.errorStatus = null;
    }

    setNewRule((prev) => ({ ...prev, [name]: name === "timeToExceed" ? parseInt(value) : value, ...errorFields }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target || {};
    const errorFields = {};

    if (!value?.includes("Error") && name === "statuses") {
      errorFields.processingError = null;
      errorFields.errorStatus = null;
    }

    setEditRule((prev) => ({ ...prev, [name]: name === "timeToExceed" ? parseInt(value) : value, ...errorFields }));
  };

  const handleAdd = () => {
    dispatch(
      reportsBehindOrdersActions.createBehindOrderRules({
        body: newRule,
        onSuccess: () => {
          setNewRule(initialRule);
          dispatch(reportsBehindOrdersActions.getBehindOrderRules());
        },
      })
    );
  };

  const handleEdit = (rule) => {
    setEditRule(rule);
  };

  const handleSave = () => {
    dispatch(
      reportsBehindOrdersActions.createBehindOrderRules({
        update: true,
        body: editRule,
        onSuccess: () => {
          setEditRule(null);
          dispatch(reportsBehindOrdersActions.getBehindOrderRules());
        },
      })
    );
  };

  const handleDeleteInit = (id) => {
    setDeleteRule(behindOrderRules?.find((item) => item.id === id));
    setConfirmationModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    dispatch(
      reportsBehindOrdersActions.deleteBehindOrderRules({
        id: deleteRule?.id,
        onSuccess: () => {
          setDeleteRule(null);
          setConfirmationModalVisible(false);
          dispatch(reportsBehindOrdersActions.getBehindOrderRules());
        },
      })
    );
  };

  const handleConfirmationModalClose = () => {
    setDeleteRule(null);
    setConfirmationModalVisible(false);
  };

  useEffect(() => {
    dispatch(reportsBehindOrdersActions.getBehindOrderRules());
    dispatch(variantsActions.getAllProcessingStatuses());
    dispatch(variantsActions.getAllProductCodes());
    dispatch(variantsActions.getAllProcessingError());
    dispatch(variantsActions.getAllProcessingErrorStatuses());
  }, []);

  return (
    <>
      <Table
        height="100%"
        columns={getColumns({
          posting,
          deleting,
          categoriesOptions,
          allProductCodes,
          allProcessingStatuses,
          allProcessingErrors,
          allProcessingErrorStatuses,
          processingErrorsMap,
          processingErrorStatusesMap,
          newRule,
          editRule,
          handleNewChange,
          handleEditChange,
          handleAdd,
          handleEdit,
          handleSave,
          handleDelete: handleDeleteInit,
        })}
        rows={[newRule, ...behindOrderRules]}
      />
      <ConfirmationModal
        show={confirmationModalVisible}
        body={
          <div>
            <div style={{ paddingBottom: 8 }}>Are you sure you want to delete this record?</div>
            <div style={{ marginBottom: 8 }}>
              <b>Category:</b>&nbsp;
              {deleteRule?.category}
            </div>
            <div style={{ marginBottom: 8 }}>
              <b>Product(s):</b>
              <br />
              {deleteRule?.products?.map(({ code, customerName }) => (
                <Fragment key={code}>
                  {customerName}
                  <br />
                </Fragment>
              ))}
            </div>
            <div style={{ marginBottom: 8 }}>
              <b>Status(es):</b>
              <br />
              {deleteRule?.statuses?.join(", ")}
            </div>
            <div>
              <b>Time To Exceed, days:</b>&nbsp;
              {deleteRule?.timeToExceed}
            </div>
          </div>
        }
        handleConfirm={handleDeleteConfirm}
        handleClose={handleConfirmationModalClose}
      />
    </>
  );
}
